import { FC, useState, useEffect, useMemo } from 'react'
import { File, ChevronRight, Download } from 'react-feather'

import {
  Button,
  useGetBulkDownloadStatus,
  useGetDocVersions,
  usePostBulkDocsDownload,
  useShowMessage,
} from '@pepper/ui'
import { downloadFileFromUrl } from '@pepper/utils/src/function/download'

import { useSelectedCompanyId } from 'utils/functions/misc'
import styles from './_.module.css'

interface Props {
  link: string
  name: string
  assignmentId: string
  assignmentCode?: number
  assignmentName?: string
}

const FilePill: FC<Props> = ({
  link,
  name,
  assignmentId,
  assignmentCode,
  assignmentName,
}) => {
  const [downloadJobId, setDownloadJobId] = useState<string>()

  const showMessage = useShowMessage()
  const companyId = useSelectedCompanyId()

  const docVersions = useGetDocVersions({
    data: { id: assignmentId, apiType: 'PEPPER_DOC', startIndex: 0 },
  })
  const bulkDownloadStatus = useGetBulkDownloadStatus({
    data: { jobId: downloadJobId, count: 1 },
    isBulkDownload: false,
  })
  const bulkDocsDownload = usePostBulkDocsDownload()

  const isDocDownloading = useMemo(() => {
    return (
      bulkDocsDownload.isLoading ||
      bulkDownloadStatus.isFetching ||
      Boolean(downloadJobId && bulkDownloadStatus.data?.status !== 'finished')
    )
  }, [
    bulkDocsDownload.isLoading,
    bulkDownloadStatus.data?.status,
    bulkDownloadStatus.isFetching,
    downloadJobId,
  ])

  useEffect(() => {
    const downloadUrl = `data:application/vnd.ms-word;base64,${bulkDownloadStatus.data?.content}`
    const fileName = `#${assignmentCode ?? assignmentId}${
      assignmentName ? ` - #${assignmentName}` : ''
    }.docx`
    if (bulkDownloadStatus.data?.status === 'finished' && downloadUrl) {
      downloadFileFromUrl({ url: downloadUrl, fileName })
      setDownloadJobId(undefined)
    }
  }, [assignmentCode, assignmentId, assignmentName, bulkDownloadStatus.data])

  const handleDownloadDocs = async () => {
    const filteredVersions =
      docVersions.data?.pages[0].versions.filter(version => version.name) ?? []
    const latestVersionId =
      filteredVersions[0]?.id ?? filteredVersions[0]?.version
    const sessionStorageKey = `version_job_id_${latestVersionId}`

    if (latestVersionId) {
      const latestVersionStorage = sessionStorage.getItem(sessionStorageKey)
      if (latestVersionStorage) {
        setDownloadJobId(latestVersionStorage)
        return
      } else {
        setDownloadJobId(undefined)
      }
    }

    bulkDocsDownload.mutate(
      {
        companyId,
        body: { data: [{ id: assignmentId, item_type: 'assignment' }] },
      },
      {
        onSuccess(data) {
          setDownloadJobId(data.id)
          sessionStorage.setItem(sessionStorageKey, data.id)
        },
        onError(error) {
          if (error instanceof Error) {
            showMessage({ type: 'error', message: error.message })
          }
        },
      }
    )
  }

  return (
    <div className={styles.fileWrap}>
      <div className={styles.fileIconWrap}>
        <File className={styles.fileIcon} />
      </div>
      <div className={styles.fileNamePill}>
        <span>{name}</span>
        <Button
          onClick={() => handleDownloadDocs()}
          className={styles.downloadWrap}
          disabled={isDocDownloading}
          loading={isDocDownloading}
          type="link"
        >
          Download <Download className={styles.downloadIcon} />
        </Button>
      </div>
      <a className={styles.goLink} href={link} target="_blank" rel="noreferrer">
        <ChevronRight className={styles.goRightIcon} />
      </a>
    </div>
  )
}

export default FilePill
